import { useEffect, useRef, useState } from "react";
import { AuthAPI } from "../../api/AuthAPI";
import { login, isLoggedIn } from "../../utils/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";

const STORAGE_KEY = "otp_phone_draft";

export default function MobileOtpForm({ onSuccess, inModal = false }) {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || "";
    } catch (_) {
      return "";
    }
  });
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [timer, setTimer] = useState(30);
  const [focused, setFocused] = useState(false);

  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { syncGuestCartAfterLogin } = useCart();
  const redirectTo =
    new URLSearchParams(location.search).get("redirect") || "/";

  useEffect(() => {
    if (isLoggedIn()) onSuccess?.();
  }, [onSuccess]);

  /* persist phone */
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, phone);
    } catch (_) {}
  }, [phone]);

  /* resend timer */
  useEffect(() => {
    if (step !== 2 || timer <= 0) return;
    const id = setInterval(() => setTimer((p) => p - 1), 1000);
    return () => clearInterval(id);
  }, [step, timer]);

  const validatePhone = (val) => {
    if (!val || val.length < 10) return "Enter a valid 10-digit mobile number";
    return "";
  };

  /* ── send OTP ── */
  const sendOtp = async () => {
    setPhoneTouched(true);
    const err = validatePhone(phone);
    setPhoneErr(err);
    if (err) return;

    try {
      setLoading(true);
      setPhoneErr("");
      await AuthAPI.sendOtpToMobile(phone);
      setStep(2);
      setTimer(30);
      setOtpDigits(["", "", "", "", "", ""]);
      setOtp("");
      setOtpErr("");
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    } catch (err) {
      setPhoneErr(
        err.response?.data?.message || "Failed to send OTP. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── verify OTP ── */
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpErr("Enter the complete 6-digit OTP");
      return;
    }
    try {
      setLoading(true);
      setOtpErr("");
      const res = await AuthAPI.verifyOtpToMobile(phone, otp);
      const data = res.data;
      login({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      await syncGuestCartAfterLogin();
      sessionStorage.removeItem(STORAGE_KEY);
      resetState();
      onSuccess?.();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setOtpErr(
        err.response?.data?.message || "Invalid OTP. Please try again.",
      );
      // shake the boxes
      setOtpDigits(["", "", "", "", "", ""]);
      setOtp("");
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setStep(1);
    setPhone("");
    setOtp("");
    setOtpDigits(["", "", "", "", "", ""]);
    setPhoneErr("");
    setOtpErr("");
    setPhoneTouched(false);
    setTimer(30);
  };

  /* ── OTP digit handlers ── */
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    setOtp(next.join(""));
    setOtpErr("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKey = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
    if (e.key === "Enter" && otp.length === 6) verifyOtp();
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const arr = pasted.split("");
    const final = [...arr, "", "", "", "", "", ""].slice(0, 6);
    setOtpDigits(final);
    setOtp(final.join(""));
    setOtpErr("");
    setTimeout(() => {
      const lastFilled = Math.min(arr.length, 5);
      inputRefs.current[lastFilled]?.focus();
    }, 30);
  };

  if (isLoggedIn()) return null;

  /* ── STEP 1 ── */
  const step1 = (
    <>
      <div style={{ marginBottom: "12px" }}>
        <label style={labelStyle}>Mobile number</label>
        <div style={{ display: "flex" }}>
          <span style={prefixStyle}>+91</span>
          <input
            type="tel"
            maxLength={10}
            value={phone}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              setPhone(v);
              if (phoneTouched) setPhoneErr(validatePhone(v));
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              setPhoneTouched(true);
              setPhoneErr(validatePhone(phone));
            }}
            onKeyDown={(e) => e.key === "Enter" && sendOtp()}
            placeholder="98765 43210"
            style={{
              flex: 1,
              height: "42px",
              padding: "0 13px",
              border: `1.5px solid ${phoneErr && phoneTouched ? "#fca5a5" : focused ? "#2563eb" : "#e5e7eb"}`,
              borderLeft: "none",
              borderRadius: "0 10px 10px 0",
              fontSize: "14px",
              color: "#111827",
              outline: "none",
              boxSizing: "border-box",
              transition: "border-color 0.15s",
              background: phoneErr && phoneTouched ? "#fff8f8" : "#fafafa",
            }}
          />
        </div>
        {phoneErr && phoneTouched && (
          <p style={fieldErrorStyle}>
            <ErrIcon /> {phoneErr}
          </p>
        )}
      </div>

      <button onClick={sendOtp} disabled={loading} style={btnStyle(loading)}>
        {loading ? (
          <>
            <Spinner /> Sending OTP…
          </>
        ) : (
          "Send OTP"
        )}
      </button>
    </>
  );

  /* ── STEP 2 ── */
  const step2 = (
    <>
      <div style={{ marginBottom: "20px" }}>
        <p style={{ fontSize: "13px", color: "#6b7280", margin: "0 0 2px" }}>
          OTP sent to
        </p>
        <p
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#111827",
            margin: 0,
          }}
        >
          +91 {phone}{" "}
          <button
            onClick={() => {
              setStep(1);
              setOtpErr("");
            }}
            style={{
              background: "none",
              border: "none",
              fontSize: "12px",
              color: "#2563eb",
              cursor: "pointer",
              padding: 0,
              marginLeft: 6,
              fontWeight: 400,
            }}
          >
            Change
          </button>
        </p>
      </div>

      <div
        style={{ display: "flex", gap: "8px", justifyContent: "space-between" }}
        onPaste={handlePaste}
      >
        {otpDigits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            maxLength={1}
            inputMode="numeric"
            value={digit}
            onChange={(e) => handleOtpChange(e.target.value, i)}
            onKeyDown={(e) => handleOtpKey(e, i)}
            style={{
              width: "42px",
              height: "50px",
              textAlign: "center",
              fontSize: "20px",
              fontWeight: 600,
              border: `1.5px solid ${otpErr ? "#fca5a5" : digit ? "#2563eb" : "#e5e7eb"}`,
              borderRadius: "10px",
              outline: "none",
              color: "#111827",
              background: otpErr ? "#fff8f8" : digit ? "#eff6ff" : "#fafafa",
              transition: "border-color 0.15s, background 0.15s",
              caretColor: "#2563eb",
            }}
          />
        ))}
      </div>

      {otpErr && (
        <p style={fieldErrorStyle}>
          <ErrIcon /> {otpErr}
        </p>
      )}

      <button onClick={verifyOtp} disabled={loading} style={btnStyle(loading)}>
        {loading ? (
          <>
            <Spinner /> Verifying…
          </>
        ) : (
          "Verify & sign in"
        )}
      </button>

      <div style={{ textAlign: "center", marginTop: "14px", fontSize: "13px" }}>
        {timer > 0 ? (
          <span style={{ color: "#9ca3af" }}>Resend OTP in {timer}s</span>
        ) : (
          <button
            onClick={sendOtp}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#2563eb",
              fontSize: "13px",
              fontWeight: 500,
              textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}
          >
            Resend OTP
          </button>
        )}
      </div>
    </>
  );

  const content = step === 1 ? step1 : step2;

  if (inModal) return content;

  /* standalone mode — own overlay */
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "100%",
          maxWidth: "380px",
          borderRadius: "20px",
          padding: "32px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#111827",
              margin: "0 0 4px",
            }}
          >
            Sign in
          </h2>
          <p style={{ fontSize: "13px", color: "#9ca3af", margin: 0 }}>
            with mobile OTP
          </p>
        </div>
        {content}
      </div>
    </div>
  );
}

/* ── small helpers ── */
function ErrIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Spinner() {
  return (
    <span
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.35)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.7s linear infinite",
        marginRight: 8,
      }}
    />
  );
}

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 500,
  color: "#374151",
  marginBottom: "6px",
  letterSpacing: "0.02em",
};
const prefixStyle = {
  height: "42px",
  padding: "0 12px",
  border: "1.5px solid #e5e7eb",
  borderRight: "none",
  borderRadius: "10px 0 0 10px",
  display: "flex",
  alignItems: "center",
  fontSize: "14px",
  color: "#6b7280",
  background: "#f3f4f6",
  userSelect: "none",
  flexShrink: 0,
};
const fieldErrorStyle = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "11.5px",
  color: "#dc2626",
  marginTop: "6px",
};
const btnStyle = (loading) => ({
  width: "100%",
  height: "42px",
  marginTop: "16px",
  borderRadius: "10px",
  border: "none",
  background: loading ? "#93c5fd" : "#2563eb",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 500,
  cursor: loading ? "not-allowed" : "pointer",
  transition: "background 0.15s",
  letterSpacing: "0.01em",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
